import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "semantic-ui-react";
import styled from "styled-components";
import {
  Device,
  FetchStreamsAPIResponse,
  Filters,
  Key,
  SearchDeviceResponse,
  SearchDevicesFiltersType,
  fetchAllMetadataKeys,
  fetchAllStreamsWithDetails,
  searchDevices,
} from "../../../../BytebeamClient";
import { useUser } from "../../../../context/User.context";
import { AtLeastOnePropRequired, Permission } from "../../../../util";
import { breakpoints } from "../../../common/breakpoints";
import { convertDecimalToRoman } from "../util";
import { PhaseData } from "./NewAction";
import PhaseContent from "./PhaseContent";
import UploadDeviceListModal from "./UploadDeviceListModal";

export const PhaseSectionContainer = styled.div`
  width: 100%;
  position: relative;
`;

export const PhasesMenuWrapper = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 40px;
  padding: 6px 0px;
  overflow-x: scroll;
  scroll-behavior: smooth;

  @media (min-width: ${breakpoints.sm}px) {
    max-width: 75vw;
  }
  @media (max-width: ${breakpoints.sm + 1}px) {
    max-width: 90vw;
  }
`;

export const AddFiltersButton = styled(Icon)`
  margin-right: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 15px !important;
  cursor: pointer;
`;

type PhaseErrorsMessage = { [key: string]: string | null };

interface PhaseControlSectionProps {
  readonly phasesData: PhaseData[];
  readonly setPhasesData: (phases: PhaseData[]) => void;
  readonly isPhasedRollout: boolean;
  readonly setIsPhasedRollout: (isPhasedRollout: boolean) => void;
}

const PhaseControlSection: React.FC<PhaseControlSectionProps> = ({
  phasesData,
  setPhasesData,
  isPhasedRollout,
  setIsPhasedRollout,
}) => {
  const { user } = useUser();
  const viewMetadataPermission: "all" | string[] =
    user?.role?.permissions?.viewMetadata;
  const permissions: Permission = user?.role?.permissions ?? {};

  const scrollRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController>(new AbortController());

  const [filters, setFilters] = useState<Filters>({});
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [selectedDevices, setSelectedDevices] = useState<SearchDeviceResponse>({
    devices: [],
    count: 0,
  });
  const [page, setPage] = useState<number>(1);
  const [devicesLoading, setDevicesLoading] = useState<boolean>(true);
  const [devices, setDevices] = useState<SearchDeviceResponse>({
    devices: [],
    count: 0,
  });
  const [phaseErrorsMessage, setPhaseErrorsMessage] =
    useState<PhaseErrorsMessage>({});
  const [activePhase, setActivePhase] = useState<string>(
    `Phase ${convertDecimalToRoman(1)}`
  );
  const [allowedFilterBys, setAllowedFilterBys] = useState<Key[]>([]);
  const [pageLimit] = useState<number>(5);
  const [metadataKeysToShow, setMetadataKeysToShow] = useState<string[]>([]);
  const [stateKeys, setStateKeys] = useState<string[]>([]);
  const [metadataKeys, setMetadataKeys] = useState<string[]>([]);
  const [detailedStreamsList, setDetailedStreamsList] =
    useState<FetchStreamsAPIResponse>({});

  const refreshDevices = useCallback(
    async (
      pageNumber: number,
      filters: AtLeastOnePropRequired<SearchDevicesFiltersType>
    ) => {
      try {
        setDevicesLoading(true);

        abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        // setTimeout here is to allow change in filters/page using setState to take affect before refreshing devices
        setTimeout(async () => {
          const status = "active";
          try {
            const devices = await searchDevices(
              filters,
              pageNumber,
              pageLimit,
              status,
              abortControllerRef.current.signal
            );
            setDevices(devices);
          } catch (error) {
            // Ignore abort errors - they're expected when cancelling previous requests
            if (error instanceof DOMException) {
              // Request was cancelled by abort controller
              return;
            }
            console.error("Failed to refresh devices:", error);
          } finally {
            setDevicesLoading(false);
          }
        });
      } catch (error) {
        // Ignore abort errors - they're expected when cancelling previous requests
        if (error instanceof DOMException) {
          return;
        }
        console.error("Failed to refresh devices:", error);
      }
    },
    [pageLimit]
  );

  const fetchMetadataKeys = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const keys = await fetchAllMetadataKeys();
      const filteredKeys = keys.filter(
        (metadata) =>
          viewMetadataPermission === "all" ||
          viewMetadataPermission.includes(metadata.key)
      );

      const idKey = { key: "id" };
      const allKeys = [idKey, ...filteredKeys];
      setAllowedFilterBys(allKeys);

      const metadataKeysArray = filteredKeys.map((k) => k.key);
      setMetadataKeys(metadataKeysArray);
    } catch (error) {
      console.error("Failed to fetch metadata keys:", error);
    }
  }, [viewMetadataPermission]);

  const fetchStateKeysAndStreams = useCallback(async () => {
    try {
      const stateKeysArray: string[] = (
        (permissions.tables["device_shadow"] as string[]) || ([] as string[])
      ).filter((key: string) => key !== "sequence");
      setStateKeys(stateKeysArray);

      const streams = await fetchAllStreamsWithDetails();
      setDetailedStreamsList(streams);
    } catch (error) {
      console.error("Failed to fetch state keys and streams:", error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onPageChange = useCallback(
    (e, { activePage }) => {
      e.preventDefault();

      setDevicesLoading(true);
      setPage(activePage as number);

      const selectedPhase = phasesData.find(
        (phase) => phase.name === activePhase
      );
      const stateFilters = selectedPhase?.info?.state_filter || [];

      const searchFilters: AtLeastOnePropRequired<SearchDevicesFiltersType> = {
        metaDatafilters: Object.keys(filters).length > 0 ? filters : {},
        ...(stateFilters.length > 0 ? { stateFilters } : {}),
      };
      refreshDevices(activePage as number, searchFilters);
    },
    [filters, refreshDevices, activePhase, phasesData]
  );

  const resetPhaseFilters = useCallback(
    (name: string) => {
      const selectedPhase = phasesData.find((phase) => phase.name === name);
      if (!selectedPhase) return;

      selectedPhase.info.device_ids = [];
      selectedPhase.info.filter = {};
      selectedPhase.info.type = "fixed-list";
      selectedPhase.info.fraction = 0;

      const updatedPhasesData: PhaseData[] = phasesData.map((phase) =>
        phase.name === name ? selectedPhase : phase
      );
      setPhasesData(updatedPhasesData);
    },
    [phasesData, setPhasesData]
  );

  const resetFilters = useCallback(
    (name?: string) => {
      setDevicesLoading(true);
      setPage(1);
      setAllowedFilterBys([]);
      setFilters({});
      setSelectedDevices({ devices: [], count: 0 });
      setAllSelected(false);
      if (name) resetPhaseFilters(name);
    },
    [resetPhaseFilters]
  );

  const updateFilters = useCallback(
    (searchFilters: SearchDevicesFiltersType, allowedFilterBy: Key[]) => {
      setPage(1);
      setDevicesLoading(true);
      setSelectedDevices({ devices: [], count: 0 });
      setAllSelected(false);

      const metadataFilters = searchFilters.metaDatafilters || {};
      refreshDevices(
        1,
        searchFilters as AtLeastOnePropRequired<SearchDevicesFiltersType>
      );

      const updatedPhasesData: PhaseData[] = phasesData.map((phase) => {
        if (phase.name === activePhase) {
          const hasFilters =
            Object.keys(metadataFilters)?.length > 0 ||
            (searchFilters.stateFilters?.length ?? 0) > 0;
          phase.info = {
            ...phase.info,
            type: hasFilters ? "filter-fraction" : "fixed-list",
            filter: (metadataFilters as Record<string, string[]>) ?? {},
            fraction: hasFilters ? 100 : 0,
          };
          if (
            searchFilters?.stateFilters &&
            searchFilters?.stateFilters?.length > 0
          ) {
            phase.info.state_filter = searchFilters.stateFilters;
          }
        }
        return phase;
      });
      setPhasesData(updatedPhasesData);
      setAllSelected(
        Boolean(
          Object.keys(metadataFilters)?.length ||
            (searchFilters.stateFilters?.length ?? 0) > 0
        )
      );
    },
    [activePhase, phasesData, refreshDevices, setPhasesData]
  );

  // Create a ref for the debounced function
  const debouncedUpdateFiltersRef = useRef(debounce(updateFilters, 1000));

  /** Update the debounced function whenever updateFilters changes
   * this is essential as if it is used directly then because of debouncing
   * it does not take latest values of states
   * even if the states are passed as dependencies in callback
   */
  useEffect(() => {
    const currentDebouncedFetch = debouncedUpdateFiltersRef.current;
    debouncedUpdateFiltersRef.current = debounce(updateFilters, 1000);

    // Cleanup function to cancel the debounced call when the component unmounts
    return () => currentDebouncedFetch.cancel();
  }, [updateFilters]);

  // simplify use for the debounced function
  const debouncedUpdateFilters = (...args) => {
    debouncedUpdateFiltersRef.current(...args);
  };

  const handlePhaseClick = useCallback(
    (name: string, phaseData?: PhaseData) => {
      setActivePhase(name);
      resetFilters();

      const selectedPhase =
        phaseData ?? phasesData.find((phase) => phase.name === name);
      if (!selectedPhase) return;

      const { type, filter, device_ids } = selectedPhase.info;
      const newDevices = device_ids?.map((id) => ({ id }) as Device) ?? [];

      const newFilters = filter ?? {};
      setFilters(newFilters);

      if (type === "filter-fraction" || type === "filter-fraction-lazy") {
        setAllSelected(Boolean(Object.keys(newFilters)?.length));
        setSelectedDevices({
          devices: newDevices,
          count: devices?.count ?? newDevices?.length,
        });
      } else if (type === "fixed-list") {
        setSelectedDevices({ devices: newDevices, count: newDevices?.length });
      }
    },
    [devices?.count, phasesData, resetFilters] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * this useEffect triggers when:
   * * initial render
   * * new phase added
   * * currently selected phase changes
   *
   * this useEffect DOES NOT trigger when:
   * * change in filters
   */
  useEffect(() => {
    const initialFetch = () => {
      fetchMetadataKeys();
      fetchStateKeysAndStreams();
      const searchFilters: AtLeastOnePropRequired<SearchDevicesFiltersType> = {
        metaDatafilters: Object.keys(filters).length > 0 ? filters : {},
      };
      refreshDevices(1, searchFilters);
    };

    if (!allowedFilterBys?.length) {
      initialFetch();
    }
  }, [filters, fetchMetadataKeys, fetchStateKeysAndStreams, refreshDevices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const phaseName = `Phase ${convertDecimalToRoman(1)}`;
    if (activePhase !== phaseName) handlePhaseClick(phaseName);
  }, [isPhasedRollout]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      const scrollable = scrollRef.current;
      scrollable.scrollLeft = scrollable.scrollWidth - scrollable.clientWidth;
    }
  }, [phasesData.length, activePhase]);

  return (
    <>
      <UploadDeviceListModal
        setIsPhasedRollout={setIsPhasedRollout}
        setPhasesData={setPhasesData}
        handlePhaseClick={handlePhaseClick}
        fetchMetadataKeys={fetchMetadataKeys}
        refreshDevices={refreshDevices}
        disable={devicesLoading || devices?.count === 0}
      />
      {phasesData.map((phase) => (
        <PhaseContent
          key={phase.id}
          phase={phase}
          phasesData={phasesData}
          setPhasesData={setPhasesData}
          activePhase={activePhase}
          phaseErrorsMessage={phaseErrorsMessage}
          setPhaseErrorsMessage={setPhaseErrorsMessage}
          allowedFilterBys={allowedFilterBys}
          setFilters={setFilters}
          debouncedUpdateFilters={debouncedUpdateFilters}
          devices={devices}
          devicesLoading={devicesLoading}
          setDevicesLoading={setDevicesLoading}
          allSelected={allSelected}
          setAllSelected={setAllSelected}
          selectedDevices={selectedDevices}
          setSelectedDevices={setSelectedDevices}
          page={page}
          pageLimit={pageLimit}
          onPageChange={onPageChange}
          metadataKeysToShow={metadataKeysToShow}
          setMetadataKeysToShow={setMetadataKeysToShow}
          isPhasedRollout={isPhasedRollout}
          user={user}
          stateKeys={stateKeys}
          metadataKeys={metadataKeys}
          detailedStreamsList={detailedStreamsList}
        />
      ))}
    </>
  );
};

export default PhaseControlSection;
